import React, { useEffect, useState } from 'react';
import { invoiceService, userService } from '../services/api';
import { Invoice, User } from '../types';
import { toast } from 'react-toastify';
import './Payments.css';

const Payments: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const data = await invoiceService.getAllInvoices();
      // Filter invoices with balance
      const withBalance = data.filter(inv => 
        parseFloat(inv.balanceAmount || '0') > 0
      );
      setInvoices(withBalance);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedInvoice || !paymentAmount) {
      toast.error('Please select an invoice and enter payment amount');
      return;
    }

    const amount = parseFloat(paymentAmount);
    const balance = parseFloat(selectedInvoice.balanceAmount || '0');

    if (amount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }

    if (amount > balance) {
      toast.error('Payment amount cannot exceed balance');
      return;
    }

    setProcessing(true);
    try {
      const newPaidAmount = parseFloat(selectedInvoice.paidAmount || '0') + amount;
      const newBalance = balance - amount;

      await invoiceService.updateInvoice(selectedInvoice.id, {
        paidAmount: newPaidAmount.toString(),
        balanceAmount: newBalance.toString(),
      });

      toast.success('Payment recorded successfully!');
      setSelectedInvoice(null);
      setPaymentAmount('');
      loadInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading payments...</div>;
  }

  return (
    <div className="payments-page">
      <h1>Manual Payment Management</h1>

      <div className="payments-container">
        <div className="invoices-list">
          <h2>Invoices with Pending Payments</h2>
          {invoices.length === 0 ? (
            <div className="no-data">No pending payments</div>
          ) : (
            <div className="invoice-cards">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className={`invoice-card ${selectedInvoice?.id === invoice.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedInvoice(invoice);
                    setPaymentAmount('');
                  }}
                >
                  <div className="invoice-header">
                    <span className="invoice-id">#{invoice.id}</span>
                    <span className={`type-badge ${invoice.paymentType.toLowerCase()}`}>
                      {invoice.paymentType}
                    </span>
                  </div>
                  <div className="invoice-details">
                    <p><strong>Name:</strong> {invoice.name}</p>
                    <p><strong>Month/Year:</strong> {invoice.paymentMonth}/{invoice.paymentYear}</p>
                    <p><strong>Paid:</strong> ₹{parseFloat(invoice.paidAmount || '0').toLocaleString()}</p>
                    <p className="balance"><strong>Balance:</strong> ₹{parseFloat(invoice.balanceAmount || '0').toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedInvoice && (
          <div className="payment-form-section">
            <h2>Record Payment</h2>
            <div className="payment-form">
              <div className="selected-invoice-info">
                <h3>Invoice #{selectedInvoice.id}</h3>
                <p><strong>Name:</strong> {selectedInvoice.name}</p>
                <p><strong>Balance Due:</strong> ₹{parseFloat(selectedInvoice.balanceAmount || '0').toLocaleString()}</p>
              </div>

              <div className="form-group">
                <label>Payment Amount (₹)</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="Enter payment amount"
                  min="0"
                  max={selectedInvoice.balanceAmount}
                  step="0.01"
                />
                <small>Maximum: ₹{parseFloat(selectedInvoice.balanceAmount || '0').toLocaleString()}</small>
              </div>

              <button
                onClick={handlePayment}
                disabled={processing || !paymentAmount}
                className="submit-payment-btn"
              >
                {processing ? 'Processing...' : 'Record Payment'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payments;

